import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, PencilLine } from 'lucide-react';
import { api } from '../lib/apiClient.ts';
import { NewProjectDialog } from '../components/NewProjectDialog.tsx';
import { MoveMeetingDialog } from '../components/editor/MoveMeetingDialog.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { useTheme } from '../context/ThemeContext.tsx';
import { MeetingRow, type MeetingListItem } from '../components/MeetingRow.tsx';
import { useTour } from '../lib/useTour.ts';
import { useUiMotion } from '../lib/useUiMotion.ts';

/**
 * FlipNum — split-flap-board number renderer. Each digit lives in
 * a clipped 1em-tall strip that holds 0–9 stacked vertically; on
 * mount the strip slides via translateY(-Nem) to land on the target
 * digit. Per-digit animation-delay creates a left-to-right cascade
 * that reads like an old airport board flipping to its next state.
 *
 * Pairs with the .flipnum / .flipnum__digit / .flipnum__strip CSS
 * in globals.css. Inherits font-size from the parent, so callers
 * just wrap it in any text-[Xpx] container and the flips scale.
 *
 * Replaces the prior useCountUp easing for the dashboard stat
 * tiles — same input shape (numeric value), more tactile output.
 */
function FlipNum({ value }: { value: number }) {
  const digits = String(Math.max(0, Math.floor(value))).split('');
  // armed = false on first paint so the strips render at translateY(0);
  // flipped to true on the next animation frame so the CSS transition
  // catches the move to the target digit. Without this two-step the
  // strips render already-positioned and there's no animation.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    setArmed(false);
    const id = requestAnimationFrame(() => setArmed(true));
    return () => cancelAnimationFrame(id);
  }, [value]);

  return (
    <span className="flipnum">
      {digits.map((ch, i) => {
        if (!/\d/.test(ch)) {
          return (
            <span key={i} className="flipnum__static">
              {ch}
            </span>
          );
        }
        const target = Number.parseInt(ch, 10);
        return (
          <span key={i} className="flipnum__digit">
            <span
              className="flipnum__strip"
              style={{
                transform: armed ? `translateY(-${target}em)` : 'translateY(0)',
                transitionDelay: `${i * 90 + 100}ms`,
              }}
            >
              {Array.from({ length: 10 }, (_, n) => (
                <span key={n}>{n}</span>
              ))}
            </span>
          </span>
        );
      })}
    </span>
  );
}

/**
 * LetterStagger — splits `text` into per-character spans, each with
 * an incremental animation-delay so the text "drops in" letter by
 * letter via the .char keyframe in globals.css. Spaces are rendered
 * as plain non-animated text so word breaks stay natural.
 *
 * Used on the dashboard hero greeting so the first name reveals
 * with a satisfying cascade rather than appearing all at once.
 */
function LetterStagger({
  text,
  baseDelayMs = 0,
  perCharMs = 40,
}: {
  text: string;
  baseDelayMs?: number;
  perCharMs?: number;
}) {
  return (
    <>
      {[...text].map((ch, i) => {
        if (ch === ' ') return ' ';
        return (
          <span
            key={i}
            className="char"
            style={{ animationDelay: `${baseDelayMs + i * perCharMs}ms` }}
          >
            {ch}
          </span>
        );
      })}
    </>
  );
}

// Exported so ProjectsListRoute can reuse the same shape — both
// pages query GET /projects?withCounts=true and render tiles via
// the shared ProjectTile component below.
export interface ProjectSummary {
  id: string;
  name: string;
  usageCount: number;
  /** Firmwide-visible meeting count: all finalized + the current
   *  user's own non-finalized (admin sees everything). Drives
   *  Active projects count + All projects tile counts. */
  meetingCount: number;
  finalizedCount: number;
  draftCount: number;
  lastActivityAt: string | null;
  /** Per-user meeting count: only meetings the current user owns
   *  or created. Drives the dashboard's "Recent projects you've
   *  worked on" list so it filters out projects the user has
   *  never touched. */
  myMeetingCount?: number;
  /** Per-user last-activity timestamp: most-recent updated_at
   *  across the user's own meetings on this project. Null when
   *  myMeetingCount is 0. */
  myLastActivityAt?: string | null;
  /** Most-common office among OJB team attendees on this project's
   *  meetings. Null when the project has no OJB attendees yet. */
  leadOffice?: string | null;
}

/**
 * Top-level dashboard. Delegates presentation to a theme-specific variant
 * (editorial / studio / utility) so each theme feels visibly different —
 * not just a color swap.
 */
export function DashboardRoute() {
  const { user } = useAuth();
  const { theme } = useTheme();

  const myDrafts = useQuery({
    queryKey: ['meetings', 'mine', 'drafts'],
    queryFn: () =>
      api.get<{ meetings: MeetingListItem[] }>('/meetings', {
        scope: 'mine',
        status: 'draft',
        limit: 10,
      }),
    enabled: !!user,
  });

  const recentFinalized = useQuery({
    queryKey: ['meetings', 'mine', 'finalized', 'recent'],
    queryFn: () =>
      api.get<{ meetings: MeetingListItem[] }>('/meetings', {
        scope: 'mine',
        status: 'finalized',
        limit: 5,
      }),
    enabled: !!user,
  });

  const projectsWithCounts = useQuery({
    queryKey: ['projects-with-counts'],
    queryFn: () =>
      api.get<{ projects: ProjectSummary[] }>('/projects', { withCounts: true, limit: 100 }),
    enabled: !!user,
  });

  const shared: DashboardProps = {
    displayName: user?.displayName ?? '-',
    draftsLoading: myDrafts.isLoading,
    draftsError: myDrafts.error,
    drafts: myDrafts.data?.meetings ?? [],
    finalizedLoading: recentFinalized.isLoading,
    finalizedError: recentFinalized.error,
    finalized: recentFinalized.data?.meetings ?? [],
    projectsLoading: projectsWithCounts.isLoading,
    projectsError: projectsWithCounts.error,
    projects: projectsWithCounts.data?.projects ?? [],
  };

  // Hardcoded to Studio — Editorial + Utility variants remain in the
  // codebase for possible future use but are no longer reachable via
  // the UI (theme picker removed pre-pilot). Belt-and-suspenders so
  // legacy localStorage values that slip past ThemeContext's
  // migration still render the current design.
  void theme;
  return <StudioDashboard {...shared} />;
}

interface DashboardProps {
  displayName: string;
  draftsLoading: boolean;
  draftsError: unknown;
  drafts: MeetingListItem[];
  finalizedLoading: boolean;
  finalizedError: unknown;
  finalized: MeetingListItem[];
  projectsLoading: boolean;
  projectsError: unknown;
  projects: ProjectSummary[];
}

/* The EDITORIAL dashboard variant (magazine layout, hairline rules,
 * masthead stat strip) was removed during the audit-fix sweep — it
 * was a pre-mockup design exploration that StudioDashboard
 * superseded. Recoverable from git history if we ever revisit. */

/* ═══════════════════════════════════════════════════════════════════════
 * STUDIO — borderless cards with soft shadows, big display numbers,
 * generous whitespace, Pine/Sage accents.
 * ═══════════════════════════════════════════════════════════════════════ */

function StudioDashboard(props: DashboardProps) {
  const totalDrafts = props.drafts.length;
  const activeProjects = props.projects.filter((p) => p.meetingCount > 0);

  // Latest 5 projects YOU'VE worked on, sorted by your most-recent
  // activity. Filters out projects the current user has never
  // touched (even if their teammates have finalized minutes there)
  // — pilot ask: the dashboard should be personalized, not a
  // firmwide rollup. Backend serves myMeetingCount + myLastActivityAt
  // per project so this stays a cheap client-side sort.
  const recentProjects = [...activeProjects]
    .filter((p) => (p.myMeetingCount ?? 0) > 0)
    .sort((a, b) => {
      const aT = a.myLastActivityAt ? Date.parse(a.myLastActivityAt) : 0;
      const bT = b.myLastActivityAt ? Date.parse(b.myLastActivityAt) : 0;
      return bT - aT;
    })
    .slice(0, 5);

  // Wire up the page-level motion utilities (.reveal, .has-light,
  // [data-tilt], .has-cursor-gutter) defined in globals.css. The
  // hook is a no-op for elements that don't opt in, so adding it
  // here just lights up any opted-in surfaces without touching
  // components that haven't adopted the utilities yet.
  useUiMotion();

  // Today, formatted "Tue 26 Apr" for the hero kicker. Recomputed
  // on each render — the dashboard mounts fresh on navigation, so
  // the value is always current without needing to track it in
  // state.
  const today = useMemo(() => {
    const d = new Date();
    const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
    const day = d.getDate();
    const month = d.toLocaleDateString(undefined, { month: 'short' });
    return `${weekday} ${day} ${month}`;
  }, []);

  // Total finalized count across all projects — feeds the hero
  // subtitle ("X drafts, Y finalized") so the greeting reflects
  // the user's current state instead of static copy.
  const totalFinalized = props.projects.reduce(
    (acc, p) => acc + (p.finalizedCount ?? 0),
    0,
  );

  // Onboarding tour for the dashboard — fires on the user's first
  // three sign-ins. Two lightweight steps pointing at the most
  // important entry points; stops before overwhelming.
  useTour({
    pageKey: 'dashboard',
    steps: [
      {
        id: 'dashboard-stats',
        title: 'Welcome to OJB Meeting Minutes',
        text: 'Drafts in flight, active projects, and recent projects you\u2019ve worked on live here. Numbers update as you go.',
        attachTo: '[data-tour="dashboard-stats"]',
        on: 'bottom',
      },
      {
        id: 'dashboard-new-meeting',
        title: 'Start a new meeting',
        text: 'Paste a transcript, an AI summary, or your own shorthand - or pull straight from your Outlook calendar. Claude turns it into OJB-formatted minutes.',
        attachTo: '[data-tour="dashboard-new-meeting"]',
        on: 'bottom',
      },
    ],
  });

  return (
    <div className="mx-auto max-w-6xl px-8 pb-16 pt-8">
      {/* Hero block — asymmetric corner radii silhouette + flat
          cloud fill + paper-grain texture + pine halo shadow. No
          tri-color accents: the tri-color bar pattern is no
          longer part of brand standards for this app. */}
      <div
        data-parallax
        className="hero-glow paper-grain relative mb-12 overflow-hidden rounded-bl-[40px] rounded-br-xl rounded-tl-xl rounded-tr-[60px] border border-pine bg-cloud px-12 py-12 motion-safe:animate-fade-in-up"
        style={{
          boxShadow:
            '0 0 32px rgba(55, 85, 66, 0.25), 0 5px 16px rgba(43, 42, 47, 0.09)',
        }}
      >
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div className="min-w-0 max-w-2xl">
            {/* Kicker carries today's short date (mockup v3.5
                "Dashboard · Tue 26 Apr") so the greeting is anchored
                in time and the page reads as a fresh "today" view. */}
            <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-pine">
              Dashboard <span className="text-pine/60">·</span>{' '}
              <span className="text-pine/80">{today}</span>
            </div>
            {/* Greeting: "Hi, " in a lighter weight, first name in
                pine font-medium for emphasis. Mockup uses <strong>
                with pine 500 to make the name pop, reading as a
                personal hand-off rather than a flat label. */}
            <h1 className="mt-3 text-[56px] font-light leading-[1.05] tracking-tight text-charcoal">
              Hi,{' '}
              <span className="font-medium text-pine">
                <LetterStagger
                  text={`${props.displayName.split(' ')[0]}.`}
                  baseDelayMs={250}
                />
              </span>
            </h1>
            {/* Subtitle: lightweight personalized status. Generic
                fallback when there's nothing in flight so the page
                doesn't look broken on a fresh account. */}
            <p className="mt-4 max-w-prose text-[17px] font-light leading-relaxed text-charcoal/80">
              {totalDrafts === 0 && totalFinalized === 0
                ? 'Pick up where you left off - paste notes or pull a meeting from your calendar to get started.'
                : null}
              {totalDrafts > 0 && (
                <>
                  <span className="font-medium text-charcoal">
                    {totalDrafts}
                  </span>{' '}
                  {totalDrafts === 1 ? 'draft' : 'drafts'} in flight
                </>
              )}
              {totalDrafts === 0 && totalFinalized > 0 && (
                <>No drafts in flight</>
              )}
              {totalFinalized > 0 && (
                <>
                  ,{' '}
                  <span className="font-medium text-charcoal">
                    {totalFinalized}
                  </span>{' '}
                  finalized so far. Pick up where you left off below.
                </>
              )}
              {totalDrafts > 0 && totalFinalized === 0 && (
                <>. Your recent work is below.</>
              )}
            </p>
          </div>
          {/* "+ New meeting" CTA \u2014 solid pine pill with white text, lifts
              on hover for a little life. Tailwind classes (not inline
              style) so the pine fill actually renders. */}
          {/* Pulse-on-load (#8): fires twice over ~3s to draw the eye
              to the primary CTA on first arrival, then stops (forwards
              keeps the final state). Motion-safe. */}
          <Link
            to="/meetings/new"
            data-tour="dashboard-new-meeting"
            className="animate-pulse-cta inline-flex items-center gap-3 rounded-full bg-pine px-6 py-3 text-[15px] font-medium text-cloud shadow-md transition-all duration-200 hover:-translate-y-1 hover:scale-[1.02] hover:bg-pine/90 hover:shadow-xl"
          >
            <span aria-hidden className="text-lg leading-none">＋</span>
            New meeting
          </Link>
        </div>
      </div>

      {/* SECTION 01 — Quick stats. Mockup uses three numbered
          dividers (01/02/03) to chapter the page; the first lives
          above the stat grid so the rhythm reads consistently from
          top to bottom. Above the fold, so it uses the page-load
          fade-in (no .reveal). */}
      <SectionDivider number="01" label="Quick stats" />

      {/* Stat cards section. Left column: Drafts stacked on Active
          projects. Right column: Recent projects mini-list (fixed 5
          slots). Grid stretches both columns to the same height so
          the two left stat cards share the list card's height evenly. */}
      <div
        data-tour="dashboard-stats"
        className="mb-12 grid gap-5 lg:grid-cols-2"
      >
        <div className="flex flex-col gap-5">
          <StudioStatCard
            to="/drafts"
            icon={<PencilLine className="h-5 w-5" />}
            label="Drafts in Progress"
            value={totalDrafts}
            className="flex-1"
            animationDelayMs={0}
          />
          <StudioStatCard
            to="/projects"
            icon={<FolderOpen className="h-5 w-5" />}
            label="Active projects"
            value={activeProjects.length}
            className="flex-1"
            animationDelayMs={80}
            variant="lite"
          />
        </div>
        <RecentProjectsCard
          projects={recentProjects}
          totalActive={activeProjects.length}
        />
      </div>

      <SectionDivider number="02" label="Recent work" reveal />

      {/* My Recent Drafts — cloud surface, thin charcoal outline.
          Wrapped in .reveal-fold so the section 3D-folds open as
          it scrolls into view (motion utility from globals.css; JS
          handler in useUiMotion). */}
      <section className="reveal-fold mb-8">
        <MeetingPillListSection
          title="My Recent Drafts"
          viewAllHref="/drafts"
          isLoading={props.draftsLoading}
          error={props.draftsError}
          meetings={props.drafts}
          emptyMessage="Your drafts will appear here once you create one."
        />
      </section>

      {/* Recently Finalized — "outlined" row variant (sage-tinted
          gradient + pine border + pine text) so finalized meetings
          read as settled/archival compared to the active drafts
          above. Same overall section chrome. */}
      <section className="reveal-fold mb-8">
        <MeetingPillListSection
          title="Recently Finalized"
          viewAllHref="/projects"
          isLoading={props.finalizedLoading}
          error={props.finalizedError}
          meetings={props.finalized}
          emptyMessage="Finalized meetings will appear here once you ship some."
          rowVariant="outlined"
        />
      </section>

      <SectionDivider number="03" label="Projects" reveal />

      {/* All projects — alphabetical grid of clickable project tiles.
          Cloud surface + charcoal outline to match the drafts /
          finalized sections above; each tile is bone-surfaced with
          pine accents (from step 3e). Scroll container holds ~5
          rows (10 tiles in 2-col layout) without blowing up the
          page height. */}
      <section className="reveal-fold">
        <AllProjectsSection projects={activeProjects} />
      </section>
    </div>
  );
}

/**
 * SectionDivider — thin charcoal hairline with a small tracked
 * chapter marker ("01 / QUICK STATS") sitting on top, breaking up
 * the dashboard into clear visual chapters. Purely decorative; zero
 * interactive bits.
 *
 * `reveal` switches from the page-load fade-in to the scroll-into-
 * view fade (.reveal utility — IntersectionObserver toggles
 * .is-visible via useUiMotion). Above-the-fold dividers (01) use
 * the page-load anim; below-the-fold dividers (02, 03) opt into
 * .reveal so they fade in as the user scrolls past them.
 */
function SectionDivider({
  number,
  label,
  reveal = false,
}: {
  number: string;
  label: string;
  reveal?: boolean;
}) {
  if (reveal) {
    return (
      <div className="reveal mb-5 mt-12 flex items-center gap-4">
        <span className="text-[10px] font-semibold tabular-nums tracking-[0.25em] text-charcoal/50">
          {number}
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-charcoal/70">
          {label}
        </span>
        <span className="h-px flex-1 bg-charcoal/20" aria-hidden />
      </div>
    );
  }
  return (
    <div
      className="mb-5 mt-2 flex items-center gap-4 motion-safe:animate-fade-in-up"
      style={{ animationDelay: '200ms' }}
    >
      <span className="text-[11px] font-semibold tabular-nums tracking-[0.25em] text-charcoal/50">
        {number}
      </span>
      <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-charcoal/70">
        {label}
      </span>
      <span className="h-px flex-1 bg-charcoal/15" aria-hidden />
    </div>
  );
}

// (StudioTint + tintTokens helpers were only used by the deleted
// StudioCard component — removed during the audit-fix sweep. The
// live StudioStatCard hardcodes its accent colors inline.)

function StudioStatCard({
  to,
  icon,
  label,
  value,
  valueSuffix,
  caption,
  className = '',
  animationDelayMs = 0,
  variant = 'pine',
}: {
  /** Route to navigate to when the card is clicked. */
  to: string;
  /** Lucide icon (or any ReactNode) rendered in a circle top-left. */
  icon: ReactNode;
  /** Small uppercase label in the top-right of the card. */
  label: string;
  /** Big stat number / value. */
  value: number;
  /** Optional suffix after the value — e.g., "meetings" for count. */
  valueSuffix?: string;
  /** Optional caption below the number — e.g., a project name. */
  caption?: string;
  /** Extra classes for the card surface — used to stretch the card
   *  (flex-1 / h-full) when it lives in a flex-col stack. */
  className?: string;
  /** Per-card animation delay so stat cards fade in with a stagger
   *  instead of popping in all at once. */
  animationDelayMs?: number;
  /**
   * Visual treatment:
   *   - 'pine' (default): solid pine surface with cream text. Use
   *     for the primary stat tile that should grab the eye (e.g.
   *     Drafts in Progress — the page's primary call-to-action).
   *   - 'lite': bone surface with pine-tinted icon circle and
   *     charcoal text. Use for secondary stats that should read as
   *     supporting info, not as a CTA. Lightens the dashboard so
   *     the primary tile stands out more.
   */
  variant?: 'pine' | 'lite';
}) {
  const isLite = variant === 'lite';
  // Surface + text. Pine variant keeps the existing pine-depth +
  // pine fill + cream text. Lite swaps to bone surface with a soft
  // charcoal border, charcoal text, and a hover-lift that picks up
  // a pine border + soft shadow.
  // Padding scaled to mockup v3.5 (22px / 24px) so the cards read
  // as headline stat tiles, not list items. Bigger gap separates
  // the icon from the value column.
  const surfaceClass = isLite
    ? 'group relative flex items-center gap-5 overflow-hidden rounded-2xl border border-charcoal/10 bg-bone px-6 py-[22px] text-charcoal shadow-sm transition-all duration-200 hover:-translate-y-1 hover:scale-[1.005] hover:border-pine/40 hover:shadow-md motion-safe:animate-fade-in-up '
    : 'pine-depth group relative flex items-center gap-5 overflow-hidden rounded-2xl border border-charcoal bg-pine px-6 py-[22px] text-cloud transition-all duration-200 hover:-translate-y-1 hover:scale-[1.005] hover:bg-pine/90 motion-safe:animate-fade-in-up ';
  // Watermark — faint pine on lite, faint cream on pine.
  const watermarkClass = isLite
    ? 'pointer-events-none absolute -bottom-3 -right-3 text-pine/10 [&>svg]:h-24 [&>svg]:w-24'
    : 'pointer-events-none absolute -bottom-3 -right-3 text-cloud/10 [&>svg]:h-24 [&>svg]:w-24';
  // Icon circle — pine-tinted bg + pine icon on lite, soft-white
  // bg + inherited cream icon on pine.
  const iconCircleClass = isLite
    ? 'flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-pine/10 text-pine'
    : 'flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cloud/15';
  // Label / value / caption colors flip per variant.
  const labelClass = isLite
    ? 'text-[11px] font-semibold uppercase tracking-[0.22em] text-pine'
    : 'text-[11px] font-semibold uppercase tracking-[0.22em] text-cloud/80';
  // Value scaled to mockup ~44px so the dashboard reads as headline
  // stats. tabular-nums + leading-[0.95] + tight tracking match the
  // .stat-card__value spec from _shared.css.
  const valueClass = isLite
    ? 'text-[44px] font-medium leading-[0.95] tracking-tight tabular-nums text-ink'
    : 'text-[44px] font-medium leading-[0.95] tracking-tight tabular-nums';
  const valueSuffixClass = isLite
    ? 'text-[12px] text-charcoal/55'
    : 'text-[12px] text-cloud/70';
  const captionClass = isLite
    ? 'mt-1 truncate text-[12px] text-charcoal/65'
    : 'mt-1 truncate text-[12px] text-cloud/80';

  return (
    <Link
      to={to}
      className={surfaceClass + className}
      style={{ animationDelay: `${animationDelayMs}ms` }}
    >
      {/* Watermark icon (#2): oversized, faint copy of the card icon
          in the bottom-right corner. Soft texture, not foreground
          content \u2014 pointer-events-none so it never hijacks clicks. */}
      <span aria-hidden className={watermarkClass}>
        {icon}
      </span>
      <div className={iconCircleClass}>{icon}</div>

      <div className="min-w-0 flex-1">
        <div className={labelClass}>{label}</div>
        <div className="mt-2 flex items-baseline gap-2">
          {/* FlipNum — split-flap-board number animation. Each digit
              lives in a clipped strip 0-9 stacked vertically; the
              strip slides to land on the target digit with a stagger
              across digits. Replaces the prior easing count-up for a
              more tactile feel that matches the mockup motion. */}
          <span className={valueClass}>
            <FlipNum value={value} />
          </span>
          {valueSuffix && <span className={valueSuffixClass}>{valueSuffix}</span>}
        </div>
        {caption && <div className={captionClass}>{caption}</div>}
      </div>
    </Link>
  );
}

/**
 * RecentProjectsCard — right-column dashboard card. Bone surface +
 * thin charcoal hairline (matches the sibling lite stat card) with
 * a clean pine eyebrow header ("Recent projects" + "N of M") and a
 * list of up to 5 most-recently-active projects rendered as full-
 * width rows with bottom borders, name on the left, relative time
 * on the right.
 *
 * Always reserves 5 slots so the card height stays stable regardless
 * of how many projects the user has — empty slots render as muted
 * placeholder rows. This keeps the 2-column grid alignment
 * predictable (the two stacked stat cards on the left stretch to
 * match this fixed height).
 *
 * Mockup v3.5 layout: dropped the History icon circle + watermark
 * (visual noise) in favor of the cleaner row-list treatment that
 * matches the .recent-projects spec in _shared.css.
 */
const RECENT_PROJECTS_SLOT_COUNT = 5;

/**
 * Format an ISO timestamp as a short relative phrase (e.g. "2h ago",
 * "Yesterday", "3d ago", "Apr 12"). Mirrors the mockup's row-meta
 * formatting. Returns an em-dash for null/invalid input so the row
 * never reads "undefined".
 */
function formatRecentTime(iso: string | null | undefined): string {
  if (!iso) return '-';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '-';
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  // Same calendar day yesterday vs. simple day-rollover heuristic
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  // Beyond a week, fall back to short month + day (e.g. "Apr 12")
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function RecentProjectsCard({
  projects,
  totalActive,
}: {
  projects: ProjectSummary[];
  /** Total active project count — drives the "N of M" header so
   *  the user can see how big a slice this list represents. */
  totalActive: number;
}) {
  return (
    <div className="flex flex-col rounded-2xl border border-charcoal/10 bg-bone px-6 py-[22px] text-charcoal shadow-sm">
      {/* Header — small uppercase pine eyebrow on the left, "N of M"
          count on the right, baseline-aligned. Replaces the prior
          icon + circle treatment with a quieter editorial header. */}
      <div className="mb-4 flex items-baseline justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-pine">
          Recent projects
        </span>
        <span className="text-[11px] font-medium tracking-[0.05em] text-charcoal/50">
          {projects.length} of {totalActive}
        </span>
      </div>

      {/* Row list — each row is a full-width Link with a hairline
          bottom border (last child no border). Hover shifts content
          padding-left + flips text to pine, so the row reads
          "selectable" without needing a fill change. */}
      <ul className="flex flex-1 flex-col">
        {Array.from({ length: RECENT_PROJECTS_SLOT_COUNT }).map((_, idx) => {
          const p = projects[idx];
          const isLast = idx === RECENT_PROJECTS_SLOT_COUNT - 1;
          const borderClass = isLast ? '' : 'border-b border-charcoal/10';
          if (!p) {
            // Placeholder slot — reserves height so the card height
            // is stable when the user has < 5 active projects.
            // Renders as an empty row (no name, no time) so the
            // bottom-border rhythm stays intact.
            return (
              <li
                key={`empty-${idx}`}
                aria-hidden
                className={`flex items-baseline justify-between py-3 ${borderClass}`}
              >
                <span className="text-[13px] text-charcoal/20">-</span>
              </li>
            );
          }
          return (
            <li key={p.id}>
              <Link
                to={`/projects/${p.id}`}
                className={`flex items-baseline justify-between gap-3 py-3 text-charcoal transition-[padding,color] duration-200 hover:pl-2 hover:text-pine ${borderClass}`}
              >
                <span className="truncate text-[14px] font-medium">
                  {p.name}
                </span>
                <span className="shrink-0 text-[11px] tracking-[0.05em] text-charcoal/55 group-hover:text-pine/70">
                  {formatRecentTime(p.myLastActivityAt ?? p.lastActivityAt)}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * MeetingPillListSection — the dashboard "My Recent Drafts" + "Recently
 * Finalized" sections. Cloud surface with a thin charcoal outline, a
 * "View all" pine pill in the header, and a list of pine-pill meeting
 * rows (each rendered by MeetingPillRow below).
 */
export function MeetingPillListSection({
  title,
  viewAllHref,
  isLoading,
  error,
  meetings,
  emptyMessage,
  rowVariant = 'filled',
  animationDelayMs,
  showMoveAction = false,
}: {
  title: string;
  viewAllHref: string;
  isLoading: boolean;
  error: unknown;
  meetings: MeetingListItem[];
  emptyMessage: string;
  /** 'filled' = solid pine rows (active work, draws attention).
   *  'outlined' = cloud rows with pine outline + pine text (settled
   *  / archival — used for the Recently Finalized list so finalized
   *  meetings read as done rather than demanding attention). */
  rowVariant?: 'filled' | 'outlined';
  /** Optional page-load fade-in delay (ms). Only used by callers
   *  (e.g. ProjectRoute) that still rely on the staggered-mount
   *  pattern. The dashboard wraps this section in .reveal-fold
   *  instead, so it omits the prop. When omitted, no inline
   *  animation is applied and the wrapper is responsible for
   *  any entrance motion. */
  animationDelayMs?: number;
  /** When true, every row gets a hover-reveal "Move" button next
   *  to the existing delete button, opening MoveMeetingDialog so
   *  the user can re-parent the meeting to a different project.
   *  Used on the project page; off by default everywhere else. */
  showMoveAction?: boolean;
}) {
  // Apply the legacy page-load fade only when a caller explicitly
  // asks for it. On the dashboard the .reveal-fold wrapper handles
  // entrance and we don't want a double-animation.
  const wrapperClass = [
    'paper-grain rounded-2xl border border-charcoal/10 bg-cloud px-7 pb-6 pt-7 shadow-sm',
    animationDelayMs !== undefined ? 'motion-safe:animate-fade-in-up' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const wrapperStyle =
    animationDelayMs !== undefined
      ? { animationDelay: `${animationDelayMs}ms` }
      : undefined;
  return (
    <div className={wrapperClass} style={wrapperStyle}>
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <h2 className="text-[22px] font-medium tracking-tight text-charcoal">
          {title}
        </h2>
        <Link
          to={viewAllHref}
          className="inline-flex items-center gap-1.5 rounded-full bg-pine px-3.5 py-1.5 text-[12px] font-medium text-cloud transition-all hover:gap-2.5 hover:bg-pine-deep"
        >
          View all <span aria-hidden>→</span>
        </Link>
      </div>

      {isLoading ? (
        <SkeletonPillList count={3} variant={rowVariant} />
      ) : error ? (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {String((error as Error)?.message ?? 'Failed to load')}
        </div>
      ) : meetings.length === 0 ? (
        <div className="rounded-xl bg-white/60 px-5 py-8 text-center text-sm text-charcoal/60">
          {emptyMessage}
        </div>
      ) : (
        // has-cursor-gutter — vertical pine bar tracks the cursor
        // in the left margin of the list (motion utility from
        // globals.css; JS handler in useUiMotion sets --gy +
        // toggles .gutter-active on mousemove). Helps the eye
        // anchor when scanning a long list of meeting pills.
        <ul className="has-cursor-gutter flex flex-col gap-2">
          {meetings.map((m) => (
            <MeetingPillRow
              key={m.id}
              meeting={m}
              variant={rowVariant}
              showMoveAction={showMoveAction}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * SkeletonPillList — shimmer placeholder that mimics the shape of a
 * MeetingPillRow while the server data is loading. Uses the shared
 * .animate-shimmer utility defined in globals.css (cloud-tinted
 * gradient sweep). Shape matches filled/outlined variants so the
 * load-to-loaded transition doesn't shift the layout.
 */
function SkeletonPillList({
  count,
  variant,
}: {
  count: number;
  variant: 'filled' | 'outlined';
}) {
  // Match the shipped MeetingPillRow variant chrome so loading
  // doesn't visually shift when real rows arrive. Filled = bone +
  // soft border; outlined = sage-tinted gradient + pine border.
  const shellClass =
    variant === 'filled'
      ? 'bg-bone border border-charcoal/[0.06]'
      : 'border border-pine/20 bg-[linear-gradient(135deg,rgba(195,198,167,0.30)_0%,rgba(195,198,167,0.12)_100%)]';
  return (
    <ul className="flex flex-col gap-2">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className={`rounded-xl ${shellClass} px-5 py-4`}>
          <div className="animate-shimmer mb-2.5 h-3.5 w-2/5 rounded bg-charcoal/10" />
          <div className="animate-shimmer h-3 w-3/5 rounded bg-charcoal/10" />
        </li>
      ))}
    </ul>
  );
}

/**
 * MeetingPillRow — each draft / finalized meeting on the dashboard
 * renders as its own rounded pill with title, project, attendee
 * count, status, and (if available) a short AI summary line-clamped
 * to 2 lines. Two visual variants:
 *   filled   → solid pine + cloud text (active work)
 *   outlined → cloud fill + pine outline + pine text (archival)
 * Draft rows also have a hover-reveal soft-delete button; finalized
 * rows do not.
 */
function MeetingPillRow({
  meeting,
  variant = 'filled',
  showMoveAction = false,
}: {
  meeting: MeetingListItem;
  variant?: 'filled' | 'outlined';
  /** Render an additional hover-reveal "Move to project" button next
   *  to the delete button, opening MoveMeetingDialog. Only the
   *  project page passes this true today. */
  showMoveAction?: boolean;
}) {
  const qc = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: () => api.post(`/meetings/${meeting.id}/soft-delete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meetings'] });
      qc.invalidateQueries({ queryKey: ['projects-with-counts'] });
    },
  });

  // Move-to-project dialog state lives inside the row so each row
  // owns its own modal instance — same pattern as the existing
  // confirm-delete flow above.
  const [isMoveOpen, setIsMoveOpen] = useState(false);

  function handleDelete(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`Delete "${meeting.title}"? You can restore from admin.`)) return;
    deleteMutation.mutate();
  }

  function handleMoveClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsMoveOpen(true);
  }

  const statusLabel = meeting.status === 'finalized' ? 'Finalized' : 'Draft';
  const attendeeLabel =
    meeting.attendeeCount && meeting.attendeeCount > 0
      ? `${meeting.attendeeCount} ${meeting.attendeeCount === 1 ? 'attendee' : 'attendees'}`
      : null;
  // Build the metadata line: project name · attendees · status · updated
  // Empty pieces dropped so the line never starts with a stray divider.
  const metaParts = [
    meeting.projectName ?? null,
    attendeeLabel,
    statusLabel,
    `Updated ${new Date(meeting.updatedAt).toLocaleDateString()}`,
  ].filter(Boolean) as string[];

  // Variant styling — both variants share the same shape + a
  // growing pine left-rule on hover so they read as a coherent
  // family. Filled (drafts) = bone surface with charcoal body +
  // pine title; outlined (finalized) = sage-tinted gradient with
  // pine body — distinct enough at a glance to tell drafts from
  // archive without changing the row dimensions.
  const isFilled = variant === 'filled';
  const pillClass = isFilled
    ? // Drafts — bone surface, soft border, hover lifts + picks up a
      // subtle shadow. Pine title (defined below) provides the
      // accent without flooding the row in pine.
      'bg-bone border border-charcoal/[0.06] hover:-translate-y-0.5 hover:border-pine/25 hover:shadow-md'
    : // Finalized — sage-tinted gradient surface (felt-not-seen),
      // pine border tone for the archival distinction.
      'border border-pine/20 hover:-translate-y-0.5 hover:shadow-md bg-[linear-gradient(135deg,rgba(195,198,167,0.30)_0%,rgba(195,198,167,0.12)_100%)]';
  const titleClass = 'text-pine';
  const metaClass = isFilled ? 'text-charcoal/65' : 'text-pine/70';
  const summaryClass = isFilled ? 'text-charcoal/75' : 'text-pine/80';
  const deleteBtnClass = isFilled
    ? 'text-charcoal/55 hover:bg-terra-cotta/15 hover:text-terra-cotta'
    : 'text-pine/60 hover:bg-terra-cotta/15 hover:text-terra-cotta';
  // Move button uses the same hover-reveal slot pattern as delete,
  // but tinted pine (positive action) instead of terra-cotta
  // (destructive). Visible for both draft AND finalized meetings —
  // a misfiled finalized meeting still needs to be re-parented.
  const moveBtnClass = isFilled
    ? 'text-charcoal/55 hover:bg-pine/10 hover:text-pine'
    : 'text-pine/60 hover:bg-pine/10 hover:text-pine';

  return (
    <li className="group relative">
      <Link
        to={`/meetings/${meeting.id}/edit`}
        // has-light — opt into the cursor-following pine glow shipped
        // in step 3a. The JS handler that toggles .is-lit + sets
        // --lx/--ly on mousemove ships in step 3f; until then the
        // class is harmless (.has-light::after stays opacity 0).
        //
        // Pine left-rule via ::before — hidden by default, scales
        // vertically into view on hover. Provides the accent moment
        // the mockup spec'd.
        className={`has-light relative flex items-start justify-between gap-4 overflow-hidden rounded-xl px-5 py-4 pl-[22px] transition-all duration-200 before:absolute before:left-0 before:top-0 before:h-full before:w-[3px] before:origin-top before:scale-y-0 before:bg-pine before:transition-transform before:duration-300 hover:before:scale-y-100 ${pillClass}`}
      >
        <div className="min-w-0 flex-1">
          <div className={`truncate text-[16px] font-medium ${titleClass}`}>
            {meeting.title || 'Untitled'}
          </div>
          <div className={`mt-1 text-[12px] ${metaClass}`}>
            {metaParts.join(' · ')}
          </div>
          {meeting.summary && (
            <div className={`mt-2 line-clamp-2 text-[13px] leading-relaxed ${summaryClass}`}>
              {meeting.summary}
            </div>
          )}
        </div>
        {showMoveAction && (
          <button
            type="button"
            onClick={handleMoveClick}
            className={`shrink-0 rounded-full p-1.5 opacity-0 transition-all group-hover:opacity-100 ${moveBtnClass}`}
            aria-label={`Move ${meeting.title} to another project`}
            title="Move to another project"
          >
            {/* Folder-arrow glyph reads as "send to a different
                bucket" without leaning on Lucide. Same visual
                weight as the delete ✕ so the two buttons sit as
                a balanced pair on hover. */}
            <span aria-hidden>⇄</span>
          </button>
        )}
        {meeting.status === 'draft' && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className={`shrink-0 rounded-full p-1.5 opacity-0 transition-all group-hover:opacity-100 disabled:opacity-50 ${deleteBtnClass}`}
            aria-label={`Delete ${meeting.title}`}
            title="Delete draft"
          >
            {deleteMutation.isPending ? '…' : '✕'}
          </button>
        )}
      </Link>
      {showMoveAction && (
        <MoveMeetingDialog
          isOpen={isMoveOpen}
          onClose={() => setIsMoveOpen(false)}
          meeting={{
            id: meeting.id,
            title: meeting.title,
            projectId: meeting.projectId,
            projectName: meeting.projectName,
            version: meeting.version,
          }}
        />
      )}
    </li>
  );
}

/**
 * AllProjectsSection — "All projects" block below the recent-work
 * lists. Visually matches MeetingPillListSection (cloud surface,
 * charcoal outline, "New project" primary pill in the header) but
 * the body is a scrollable grid of project tiles instead of a
 * vertical list. Alphabetical order per 2d.iii.
 */
function AllProjectsSection({ projects }: { projects: ProjectSummary[] }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const sorted = [...projects].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );

  return (
    <>
      <div className="paper-grain rounded-2xl border border-charcoal/10 bg-cloud px-7 pb-6 pt-7 shadow-sm">
        <div className="mb-5 flex items-baseline justify-between gap-3">
          <h2 className="text-[22px] font-medium tracking-tight text-charcoal">
            All projects
          </h2>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full bg-pine px-3.5 py-1.5 text-[12px] font-medium text-cloud transition-all hover:gap-2 hover:bg-pine-deep"
          >
            <span aria-hidden>+</span> New project
          </button>
        </div>

        {sorted.length === 0 ? (
          <div className="rounded-xl bg-white/60 px-5 py-8 text-center text-sm text-charcoal/60">
            No projects yet. Click &ldquo;+ New project&rdquo; to create one,
            or start a meeting to auto-create.
          </div>
        ) : (
          // Card-grows-to-fit: no max-height + overflow scrolling.
          // Pilot feedback: scrollbars on cards feel like trapped
          // content. The page itself can scroll; the card lets all
          // tiles render and the user uses the page scroll to reach
          // any beyond the fold.
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {sorted.map((p) => (
              <ProjectTile key={p.id} project={p} />
            ))}
          </ul>
        )}
      </div>

      <NewProjectDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}

/**
 * ProjectTile — bone-surfaced tile for the "All projects" grid. Shows
 * the project name (pine accent), meeting count split (drafts /
 * finalized), lead office (when known), and last activity date.
 * Entire tile is a clickable Link to the project detail page.
 *
 * Visual treatment:
 *   - bone surface w/ charcoal/10 hairline border
 *   - pine title + pine arrow
 *   - hover: 4° tilt toward cursor (data-tilt) + soft cursor-tracking
 *     pine glow (has-light) + lift + pine-tinted border
 * The tilt + glow are wired by useUiMotion() at the StudioDashboard
 * level — adding the className/attribute here is the only thing the
 * tile itself has to know about.
 */
// Exported so the ProjectsList page can render the same tile pattern
// as the dashboard's "All projects" grid. Identical chrome — bone
// surface, pine accents, hover lift + cursor-tracking glow.
export function ProjectTile({ project }: { project: ProjectSummary }) {
  const pieces: string[] = [
    `${project.meetingCount} ${project.meetingCount === 1 ? 'meeting' : 'meetings'}`,
  ];
  if (project.draftCount > 0) {
    pieces.push(`${project.draftCount} draft${project.draftCount === 1 ? '' : 's'}`);
  }
  if (project.finalizedCount > 0) {
    pieces.push(`${project.finalizedCount} finalized`);
  }
  return (
    <li>
      <Link
        to={`/projects/${project.id}`}
        data-tilt
        className="has-light group flex h-full flex-col justify-between gap-2.5 rounded-xl border border-charcoal/10 bg-bone px-5 py-4 text-charcoal transition-[border-color,box-shadow] duration-200 hover:border-pine/30 hover:shadow-[0_8px_24px_rgba(55,85,66,0.12)]"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 truncate text-[15px] font-medium text-pine">
            {project.name}
          </div>
          <span
            aria-hidden
            className="shrink-0 text-pine/60 transition-transform group-hover:translate-x-0.5 group-hover:text-pine"
          >
            →
          </span>
        </div>
        <div className="text-[12px] text-charcoal/70">{pieces.join(' · ')}</div>
        <div className="flex items-center justify-between gap-2 text-[10px] font-medium uppercase tracking-[0.18em] text-pine/85">
          <span className="truncate">
            {project.leadOffice ? `Lead: ${project.leadOffice}` : 'Lead: -'}
          </span>
          <span className="shrink-0 text-charcoal/55 normal-case tracking-normal">
            {project.lastActivityAt
              ? `Last ${new Date(project.lastActivityAt).toLocaleDateString()}`
              : 'No activity yet'}
          </span>
        </div>
      </Link>
    </li>
  );
}

/* StudioCard + StudioMeetingList were dead helpers from an earlier
 * iteration of StudioDashboard — the live version uses inline cards
 * and MeetingPillListSection now. Removed during audit-fix sweep. */

/* The UTILITY dashboard variant (data-dense, Linear / Notion feel)
 * was removed during the audit-fix sweep — pre-mockup design
 * exploration that StudioDashboard superseded. Recoverable from git
 * history if we ever revisit. */

/* ═══════════════════════════════════════════════════════════════════════
 * Shared sub-components (MeetingList, ProjectsPicker, loading / error)
 * ═══════════════════════════════════════════════════════════════════════ */

interface MeetingListProps {
  isLoading: boolean;
  error: unknown;
  meetings: MeetingListItem[];
  emptyMessage: string;
}

export function MeetingList({ isLoading, error, meetings, emptyMessage }: MeetingListProps) {
  // Used by DraftsRoute (the utility-list pattern) and the legacy
  // theme variants in this file. Mockup .drafts-list spec: bone bg
  // + charcoal/10 border + rounded-[16px] outer + per-row charcoal/8
  // bottom border (handled by divide-y) + pine-soft hover wash.
  if (isLoading) {
    return (
      <div className="rounded-[16px] border border-charcoal/10 bg-bone p-10 text-center text-[13px] italic text-charcoal/55">
        Loading…
      </div>
    );
  }
  if (error) {
    // Mockup .state-card--error: terra-cotta-tinted error chrome
    // matching the rest of the app's blocking-error family.
    return (
      <div className="rounded-[16px] border border-terra-cotta/30 bg-terra-cotta/[0.08] p-6 text-[13px] text-terra-cotta">
        Failed to load: {String((error as Error).message)}
      </div>
    );
  }
  if (meetings.length === 0) {
    // Mockup .state-card--empty: dashed charcoal/20 + bone-tinted bg
    // + 14px charcoal/55. Inviting nudge, not a "broken" indicator.
    return (
      <div className="rounded-[16px] border border-dashed border-charcoal/20 bg-bone/60 p-10 text-center text-[14px] text-charcoal/55">
        {emptyMessage}
      </div>
    );
  }
  return (
    <ul className="divide-y divide-charcoal/8 overflow-hidden rounded-[16px] border border-charcoal/10 bg-bone">
      {meetings.map((m) => (
        <MeetingRow key={m.id} meeting={m} />
      ))}
    </ul>
  );
}

// (ProjectsPicker, Loading, ErrorBlock, and greeting helpers were
// scaffolding for the deleted Editorial/Utility/Studio dashboard
// variants. The live StudioDashboard handles project selection
// inline via AllProjectsSection and uses StudioCard's own loading
// states, so these are unreferenced. Removed during audit-fix
// sweep — recoverable from git history if needed.)
